import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = '/api';

// Use Bearer token from localStorage instead of cookies
axios.defaults.withCredentials = false;
axios.interceptors.request.use((config) => {
  config.withCredentials = false;
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh on 401 (token expired)
let isRefreshing = false;
let failedQueue = [];
const processQueue = (error, token = null) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve(token));
  failedQueue = [];
};
axios.interceptors.response.use(r => r, async (error) => {
  const orig = error.config;
  if (error.response?.status === 401 && !orig._retry && !orig.url?.includes('/auth/login') && !orig.url?.includes('/auth/refresh')) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => { orig.headers.Authorization = `Bearer ${token}`; return axios(orig); });
    }
    orig._retry = true;
    isRefreshing = true;
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) { isRefreshing = false; return Promise.reject(error); }
    try {
      const { data } = await axios.post(`${API}/auth/refresh`, {}, { headers: { Authorization: `Bearer ${refreshToken}` }, _retry: true });
      localStorage.setItem('access_token', data.access_token);
      processQueue(null, data.access_token);
      orig.headers.Authorization = `Bearer ${data.access_token}`;
      return axios(orig);
    } catch (e) {
      processQueue(e, null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
    }
  }
  return Promise.reject(error);
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMode, setActiveMode] = useState('buyer');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      setActiveMode(data.active_mode || (data.role === 'buyer' ? 'buyer' : 'seller'));
    } catch (e) {
      // Token expired — try refresh
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API}/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${refreshToken}` }
          });
          if (data.access_token) {
            localStorage.setItem('access_token', data.access_token);
            const { data: userData } = await axios.get(`${API}/auth/me`);
            setUser(userData);
            setActiveMode(userData.active_mode || (userData.role === 'buyer' ? 'buyer' : 'seller'));
          }
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          setUser(null);
        }
      } else {
        localStorage.removeItem('access_token');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.access_token) localStorage.setItem('access_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
    setUser(data.user || data);
    const u = data.user || data;
    setActiveMode(u.active_mode || (u.role === 'buyer' ? 'buyer' : 'seller'));
    return data;
  };

  const register = async (email, password, name, role = 'buyer') => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, role });
    if (data.access_token) localStorage.setItem('access_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
    setUser(data.user || data);
    const u = data.user || data;
    setActiveMode(u.active_mode || (u.role === 'buyer' ? 'buyer' : 'seller'));
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setActiveMode('buyer');
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const switchMode = useCallback(async (mode) => {
    try {
      await axios.put(`${API}/users/mode`, { mode });
      setActiveMode(mode);
    } catch (e) {
      console.error('Error switching mode:', e);
    }
  }, []);

  const becomeSeller = useCallback(async (consentLiability = false, consentIntermediary = false) => {
    const { data } = await axios.post(`${API}/users/become-seller`, {
      consent_liability: consentLiability,
      consent_intermediary: consentIntermediary
    });
    setUser(prev => ({ ...prev, ...data.user }));
    setActiveMode('seller');
    return data;
  }, []);

  const isSeller = user?.role && ['seller', 'seller_basic', 'seller_premium', 'elite_breeder', 'admin'].includes(user.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const isAnyAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    activeMode: isSuperAdmin ? 'admin' : activeMode,
    switchMode,
    becomeSeller,
    isAuthenticated: !!user,
    isSeller: isSuperAdmin ? false : isSeller,
    isSellerMode: isSuperAdmin ? false : (activeMode === 'seller' && isSeller),
    isBuyerMode: isSuperAdmin ? false : (activeMode === 'buyer' || !isSeller),
    isPremium: user?.subscription && ['premium', 'elite'].includes(user.subscription),
    hasAI: user?.subscription && ['basic', 'premium', 'elite'].includes(user.subscription),
    isAdmin: isAnyAdmin,
    isSuperAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
